import { useEffect, useState } from 'react';
import { Badge, Form, Stack } from 'react-bootstrap';
import { BsCart, BsFillPersonFill } from 'react-icons/bs';
import { LuChefHat } from 'react-icons/lu';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import { useCartContext } from '../../../../context/CartContext';
import { useToast } from '../../../../context/ToastContext';
import { useDestroyRegistrationMutation } from '../../../../services/endpoints/content/registrations';
import {
  useGetAllOrdersQuery,
  useUpsertOrderMutation
} from '../../../../services/endpoints/orders/orders';
import { Child } from '../../../../services/endpoints/people/user';
import {
  useAddWaitlistMutation,
  useArchiveWaitlistMutation,
  useGetAllWaitlistsQuery
} from '../../../../services/endpoints/schedule/waitlist';
import { getCurrentUser } from '../../../../services/helper';
import { GetQueryParams, GetQueryParamsWithFilterArray } from '../../../../types/api.type';
import { Course } from '../../../../types/course.type';
import { Lesson } from '../../../../types/lesson.type';
import { Order } from '../../../../types/orders.type';
import { Registration } from '../../../../types/registration.type';
import { Waitlist } from '../../../../types/waitlist.type';
import { formatToCurrency, getErrorMessage } from '../../../../utils/utils';
import './CourseRegister.css';

interface CourseRegisterProps {
  course: Course;
  userChildren: Child[];
  classesRemaining: Lesson[];
  hideOverlay: () => void;
  addChild: () => void;
  selectedChildIds: number[];
  onSelectedChildIdsChange: (ids: number[]) => void;
}

// Waitlist status strings come straight from the API's WAITLIST_STATUS const
// via the model getter: pending | invited | enrolled | expired | denied | archived.
const WAITLIST_STATUS = {
  pending: 'pending',
  invited: 'invited',
  enrolled: 'enrolled',
  expired: 'expired',
  denied: 'denied',
  archived: 'archived'
} as const;

const WAITLIST_STATUS_BY_CODE = [
  WAITLIST_STATUS.pending,
  WAITLIST_STATUS.invited,
  WAITLIST_STATUS.enrolled,
  WAITLIST_STATUS.expired,
  WAITLIST_STATUS.denied,
  WAITLIST_STATUS.archived
] as const;

// API usually returns status as a string via the model getter; tolerate numeric enums too.
function normalizeWaitlistStatus(status: unknown): string {
  if (typeof status === 'number') {
    return WAITLIST_STATUS_BY_CODE[status] ?? '';
  }
  return String(status ?? '').toLowerCase();
}

// How a given child currently relates to this course.
type ChildEnrollmentState =
    | { kind: 'registered'; registration: Registration }
    | { kind: 'cart'; registration: Registration }
    | { kind: 'invited'; waitlist: Waitlist }      // accepted off the waitlist — can check out
    | { kind: 'pending'; waitlist: Waitlist }      // waiting
    | { kind: 'denied'; waitlist: Waitlist }       // denied, still on the list
    | { kind: 'archived'; waitlist: Waitlist }     // removed — can rejoin
    | { kind: 'available' };                       // no relationship yet

function getCourseValues(
    course: Course,
    courseChildrenCount: number,
    classesRemaining: Lesson[]
): any {
  let values = {
    total: 0,
    prorated: 0
  };

  values.total = course.lessons.length * course.price_per_class * courseChildrenCount;
  values.prorated = course.can_be_prorated
      ? classesRemaining.length * course.price_per_class * courseChildrenCount
      : values.total;
  return values;
}

const CourseRegister: React.FC<CourseRegisterProps> = ({
                                                         course,
                                                         userChildren,
                                                         hideOverlay,
                                                         addChild,
                                                         classesRemaining,
                                                         selectedChildIds,
                                                         onSelectedChildIdsChange
                                                       }) => {
  const currentUser = getCurrentUser();
  const cartContext = useCartContext();
  const params = useParams();
  // Prefer the loaded course id; fall back to the route param.
  const courseId = course.id ?? Number(params.id);

  // Orders support parent_id + course_id scoping.
  const orderFilterBy = currentUser
      ? [
        { field: 'parent_id', operator: 'equals', value: currentUser.id },
        { field: 'course_id', operator: 'equals', value: courseId }
      ]
      : [];

  // Scope by course_id; API forces parent_id for customers so only own rows return.
  const waitlistFilterBy = currentUser
      ? [{ field: 'course_id', operator: 'equals', value: courseId }]
      : [];

  // Paid registrations: explicit `completed` scope (transaction_id IS NOT NULL).
  // The API's getAllOrders does NOT default to completed when no scope is sent —
  // it returns all orders. Without this filter, in-cart registrations would
  // appear as 'registered' to the classifier.
  const completedOrdersFilters: GetQueryParamsWithFilterArray = {
    scope: 'completed',
    data: { ...(currentUser ? { filterBy: orderFilterBy } : {}) }
  };
  // In-cart registrations: explicitly request the `incomplete` scope.
  const incompleteOrdersFilters: GetQueryParamsWithFilterArray = {
    scope: 'incomplete',
    data: { ...(currentUser ? { filterBy: orderFilterBy } : {}) }
  };
  // Waitlist: `all` scope so every status (incl. invited/denied/archived)
  // comes back; the classifier sorts them per child.
  // Request enough rows for a household (API defaults to 10 if omitted).
  const waitlistFilters: GetQueryParams = {
    scope: 'all',
    data: {
      ...(currentUser
          ? { filterBy: waitlistFilterBy, page: 1, entries: 100 }
          : {})
    }
  };

  const {
    data: completedOrders,
    isFetching,
    refetch: refetchCompleted
  } = useGetAllOrdersQuery(completedOrdersFilters, { skip: !currentUser });

  const {
    data: incompleteOrders,
    refetch: refetchIncomplete
  } = useGetAllOrdersQuery(incompleteOrdersFilters, { skip: !currentUser });

  const {
    data: waitlists,
    isError: isWaitlistsError,
    refetch: refetchWaitlists
  } = useGetAllWaitlistsQuery(waitlistFilters, {
    skip: !currentUser,
    // Avoid stale 403/empty cache from before parent waitlist access was enabled.
    refetchOnMountOrArgChange: true
  });

  const [removeRegistration] = useDestroyRegistrationMutation();
  const [archiveWaitlist] = useArchiveWaitlistMutation();
  const [register, { isLoading: isRegistering }] = useUpsertOrderMutation();
  const [waitlist, { isLoading: isWaitlisting }] = useAddWaitlistMutation();
  const [order, setOrder] = useState<Order>();

  const navigate = useNavigate();
  const { addToast } = useToast();

  // On fetch failure do not fall back to [] — that misclassifies invited kids as
  // available and recreates the sold-out waitlist loop.
  const waitlistEntries: Waitlist[] | undefined = isWaitlistsError
      ? undefined
      : (waitlists?.results as Waitlist[] | undefined);
  const waitlistsReady = !currentUser || (!isWaitlistsError && waitlists !== undefined);

  const invitedChildIds = (waitlistEntries ?? [])
      .filter((w) => normalizeWaitlistStatus(w.status) === WAITLIST_STATUS.invited)
      .map((w) => Number(w.child_id))
      .filter((id) => userChildren.some((c) => c.id != null && Number(c.id) === id));
  const isInviteFlow = invitedChildIds.length > 0;

  // Auto-select invited children when this is an invite checkout; otherwise
  // auto-select the sole child when nothing is selected yet.
  useEffect(() => {
    if (!waitlistsReady || waitlistEntries === undefined) return;

    if (invitedChildIds.length > 0) {
      const same =
          invitedChildIds.length === selectedChildIds.length &&
          invitedChildIds.every((id) => selectedChildIds.includes(id));
      if (!same) {
        onSelectedChildIdsChange(invitedChildIds);
      }
      return;
    }

    if (userChildren?.length === 1 && selectedChildIds.length === 0) {
      const onlyChild = userChildren[0];
      if (onlyChild?.id) {
        onSelectedChildIdsChange([onlyChild.id]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitlistsReady, waitlistEntries, invitedChildIds.join(','), userChildren]);

  useEffect(() => {
    if (!isFetching && completedOrders && completedOrders.results) {
      if (completedOrders.results.length > 0) {
        setOrder(completedOrders.results[0]);
      }
    }
  }, [completedOrders, isFetching]);

  function isCourseSoldOut(course: Course): boolean {
    const capacity = +course.enrollment?.split('/')[1];
    const enrollments = +course.enrollment?.split('/')[0];
    return enrollments >= capacity;
  }

  // Registrations from paid (completed) orders.
  const paidRegistrations: Registration[] =
      (completedOrders?.results as Order[] | undefined)?.flatMap(
          (o) => o.registrations ?? []
      ) ?? [];

  // Registrations sitting in incomplete (in-cart, unpaid) orders.
  const cartRegistrations: Registration[] =
      (incompleteOrders?.results as Order[] | undefined)?.flatMap(
          (o) => o.registrations ?? []
      ) ?? [];

  // Classify a child against this course.
  // Priority: paid registration > in-cart > waitlist (by status).
  const getChildState = (childId?: number): ChildEnrollmentState => {
    if (!childId) return { kind: 'available' };

    const paid = paidRegistrations.find(
        (r) => Number(r.child_id) === Number(childId) && Number(r.course_id) === Number(course.id)
    );
    if (paid) return { kind: 'registered', registration: paid };

    const inCart = cartRegistrations.find(
        (r) => Number(r.child_id) === Number(childId) && Number(r.course_id) === Number(course.id)
    );
    if (inCart) return { kind: 'cart', registration: inCart };

    // Waitlist still loading or failed — buckets are gated by waitlistsReady.
    if (waitlistEntries === undefined) return { kind: 'available' };

    const wl = waitlistEntries.find((w) => Number(w.child_id) === Number(childId));
    if (wl) {
      const status = normalizeWaitlistStatus(wl.status);
      switch (status) {
        case WAITLIST_STATUS.invited:
          return { kind: 'invited', waitlist: wl };
        case WAITLIST_STATUS.pending:
          return { kind: 'pending', waitlist: wl };
        case WAITLIST_STATUS.denied:
          return { kind: 'denied', waitlist: wl };
        case WAITLIST_STATUS.archived:
          return { kind: 'archived', waitlist: wl };
          // enrolled / expired: not an active waitlist row — fall through.
        default:
          return { kind: 'available' };
      }
    }

    return { kind: 'available' };
  };

  // Split the current selection into the two submit buckets.
  // invited children check out; available children go to cart or waitlist
  // depending on whether the session has room.
  // When waitlist status failed to load, leave both buckets empty so submit is blocked.
  // Invite flow: only invited children may check out; siblings cannot join waitlist here.
  const sessionFull = isCourseSoldOut(course);
  const selectedStates = selectedChildIds.map((id) => ({
    id,
    kind: getChildState(id).kind
  }));
  const checkoutBucket = waitlistsReady
      ? selectedStates
          .filter((s) => s.kind === 'invited' || (s.kind === 'available' && !sessionFull && !isInviteFlow))
          .map((s) => s.id)
      : [];
  const waitlistBucket = waitlistsReady
      ? selectedStates
          .filter((s) => s.kind === 'available' && sessionFull && !isInviteFlow)
          .map((s) => s.id)
      : [];

  // Tuition / student count: in-cart kids for this course + newly selected checkout kids.
  const courseCartChildIds = cartRegistrations
      .filter((r) => Number(r.course_id) === Number(course.id) && r.child_id != null)
      .map((r) => Number(r.child_id));
  const billableChildCount = Array.from(
      new Set([...courseCartChildIds, ...checkoutBucket])
  ).length;
  const courseValues = getCourseValues(course, billableChildCount, classesRemaining);

  // Submit button label reflects what is actually selected.
  const getSubmitLabel = (): string => {
    if (checkoutBucket.length > 0 && waitlistBucket.length > 0) return 'Submit';
    if (waitlistBucket.length > 0) return 'Add to Waitlist';
    if (checkoutBucket.length > 0) return 'Checkout';
    return 'Submit';
  };

  const registerChildren = async (childIds: number[]) => {
    try {
      if (course?.id && currentUser?.id && childIds.length > 0) {
        let orderPayload = {
          course_id: course?.id,
          parent_id: currentUser?.id ?? 0,
          child_ids: childIds
        };
        let result = await register(orderPayload).unwrap();

        let count = result?.order?.registrations?.length ?? 0;
        if (count > 0 && result?.order?.course_id) {
          cartContext.addCourses([result.order.course_id]);
        }
        setOrder(result.order);
        refetchIncomplete();
      }
    } catch (error) {
      addToast(getErrorMessage(error), 'error');
    }
  };

  const registerWaitListChildren = async (childIds: number[]) => {
    try {
      if (course?.id && childIds.length > 0) {
        const results = await Promise.all(
            childIds.map((childId) => {
              const orderPayload = {
                course_id: course.id,
                child_id: childId
              };
              return waitlist(orderPayload).unwrap();
            })
        );

        const newlyAdded = results.filter((r) => !r.alreadyExisted).length;
        const alreadyOn = results.filter((r) => r.alreadyExisted).length;

        if (newlyAdded > 0 && alreadyOn === 0) {
          addToast(
              newlyAdded === 1
                  ? 'Added to waitlist!'
                  : `${newlyAdded} children added to waitlist.`,
              'success'
          );
        } else if (newlyAdded === 0 && alreadyOn > 0) {
          addToast(
              alreadyOn === 1
                  ? 'Already on the waitlist.'
                  : `All ${alreadyOn} children are already on the waitlist.`,
              'info'
          );
        } else {
          addToast(
              `${newlyAdded} added to waitlist. ${alreadyOn} already on the list.`,
              'success'
          );
        }
        refetchWaitlists();
        return;
      }
    } catch (error) {
      addToast(getErrorMessage(error), 'error');
    }
  };

  const toggleChildId = (childId?: number) => {
    if (!childId) return;
    // Invite checkout locks selection to invited children only.
    if (isInviteFlow) return;
    const idx = selectedChildIds.indexOf(childId);
    if (idx !== -1) {
      onSelectedChildIdsChange(selectedChildIds.filter((id) => id !== childId));
    } else {
      onSelectedChildIdsChange([...selectedChildIds, childId]);
    }
  };

  // Removes an in-cart (unpaid) registration. Never used for paid registrations.
  const removeCartRegistration = async (registration: Registration) => {
    if (registration?.id === undefined) return;
    try {
      await removeRegistration(registration.id).unwrap();
      await refetchIncomplete();
      const parentOrder = (incompleteOrders?.results as Order[] | undefined)?.find(
          (o) => o.id === registration.order_id
      );
      const siblings =
          parentOrder?.registrations?.filter((r) => r.id !== registration.id) ?? [];
      if (siblings.length === 0) {
        cartContext.removeCourses([course.id]);
      }
      addToast('Removed from your cart.', 'success');
    } catch (error) {
      addToast(getErrorMessage(error), 'error');
    }
  };

  const leaveWaitlist = async (waitlistId?: number) => {
    if (waitlistId === undefined) return;
    try {
      await archiveWaitlist(waitlistId).unwrap();
      await refetchWaitlists();
      addToast('Removed from the waitlist.', 'success');
    } catch (error) {
      addToast(getErrorMessage(error), 'error');
    }
  };

  // Rejoin = add a fresh waitlist entry. The API's duplicate check handles the
  // case where a stale row still exists.
  const rejoinWaitlist = async (childId?: number) => {
    if (childId === undefined) return;
    await registerWaitListChildren([childId]);
  };

  const isSubmitting = isRegistering || isWaitlisting;
  const childrenSelectedCount = billableChildCount;
  // Cart removals persist immediately; remaining in-cart kids (no new selection)
  // are still a valid "done / return to cart" state for edit-from-cart.
  const courseHasCartKids = courseCartChildIds.length > 0;
  const canSubmitOrReturn =
      checkoutBucket.length > 0 || waitlistBucket.length > 0 || courseHasCartKids;

  void refetchCompleted;

  // One submit handler: routes each selection bucket to the right action.
  const handleSubmit = async (navigateToCart: boolean) => {
    if (checkoutBucket.length > 0) {
      await registerChildren(checkoutBucket);
    }
    if (waitlistBucket.length > 0) {
      await registerWaitListChildren(waitlistBucket);
    }
    hideOverlay();
    // New checkout additions, or edit-from-cart with kids still on the order.
    if (navigateToCart && (checkoutBucket.length > 0 || courseHasCartKids)) {
      navigate('/cart');
    }
  };

  const renderChildRow = (child: Child, index: number) => {
    const state = getChildState(child.id);

    switch (state.kind) {
      case 'registered':
        return (
            <Stack
                key={index}
                direction="horizontal"
                gap={2}
                className="child-status child-status--registered align-items-center"
            >
              <b>{child.full_name}</b>
              <Badge
                  className="d-inline-flex align-items-center gap-1"
                  style={{ backgroundColor: '#b4c634', color: '#fff' }}
              >
                <LuChefHat /> Enrolled
              </Badge>
            </Stack>
        );

      case 'cart':
        return (
            <Stack
                key={index}
                direction="horizontal"
                gap={2}
                className="child-status child-status--cart align-items-center"
            >
              <b>{child.full_name}</b>
              <Badge
                  className="d-inline-flex align-items-center gap-1"
                  style={{ backgroundColor: '#ffc107', color: '#ffffff' }}
              >
                <BsCart /> In your cart
              </Badge>
              <span
                  className="child-status-action text-danger"
                  role="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => removeCartRegistration(state.registration)}
              >
                Remove from cart
              </span>
            </Stack>
        );

      case 'invited':
        // Accepted off the waitlist — auto-selected and locked for invite checkout.
        return (
            <Stack
                key={index}
                direction="horizontal"
                gap={2}
                className="child-status child-status--invited align-items-center"
            >
              <Form.Check
                  type="checkbox"
                  id={`child-${index}`}
                  label={`${child.first_name} ${child.last_name}`}
                  value={child.id}
                  checked={selectedChildIds.includes(child.id ?? -1)}
                  disabled
                  onChange={() => {}}
              />
              <Badge
                  className="child-status-badge--invited"
                  style={{ backgroundColor: '#22c55e', color: '#fff' }}
              >
                Spot available!
              </Badge>
            </Stack>
        );

      case 'pending':
        return (
            <Stack
                key={index}
                direction="horizontal"
                gap={2}
                className="child-status child-status--pending align-items-center"
            >
              <b>{child.full_name}</b>
              <Badge style={{ backgroundColor: '#2b4e64', color: '#fff' }}>
                On the waitlist
              </Badge>
              <span
                  className="child-status-action text-danger"
                  role="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => leaveWaitlist(state.waitlist.id)}
              >
                Leave waitlist
              </span>
            </Stack>
        );

      case 'denied':
        return (
            <Stack
                key={index}
                direction="horizontal"
                gap={2}
                className="child-status child-status--denied align-items-center"
            >
              <b>{child.full_name}</b>
              <Badge style={{ backgroundColor: '#ff4136', color: '#fff' }}>
                Waitlist denied
              </Badge>
              <span
                  className="child-status-action text-danger"
                  role="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => leaveWaitlist(state.waitlist.id)}
              >
                Leave waitlist
              </span>
            </Stack>
        );

      case 'archived':
        return (
            <Stack
                key={index}
                direction="horizontal"
                gap={2}
                className="child-status child-status--archived align-items-center"
            >
              <b className="text-muted">{child.full_name}</b>
              <Badge style={{ backgroundColor: '#5e6469', color: '#fff' }}>
                Removed from waitlist
              </Badge>
              <span
                  className="child-status-action text-primary"
                  role="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => rejoinWaitlist(child.id)}
              >
                Rejoin waitlist
              </span>
            </Stack>
        );

      case 'available':
      default:
        // During invite checkout, siblings who were not invited cannot be added.
        if (isInviteFlow) {
          return (
              <Stack
                  key={index}
                  direction="horizontal"
                  gap={2}
                  className="child-status child-status--blocked align-items-center"
              >
                <Form.Check
                    type="checkbox"
                    id={`child-${index}`}
                    label={`${child.first_name} ${child.last_name}`}
                    value={child.id}
                    checked={false}
                    disabled
                    onChange={() => {}}
                />
                <Badge style={{ backgroundColor: '#5e6469', color: '#fff' }}>
                  Not invited
                </Badge>
              </Stack>
          );
        }
        return (
            <Form.Check
                key={index}
                type="checkbox"
                id={`child-${index}`}
                label={`${child.first_name} ${child.last_name}`}
                value={child.id}
                checked={selectedChildIds.includes(child.id ?? -1)}
                onChange={() => {}}
                onClick={() => toggleChildId(child.id)}
            />
        );
    }
  };

  return (
      <>
        {course && (
            <div className="p-2">
              <div className="d-flex align-items-start justify-content-between gap-2">
                <span className="fw-bold text-info h2 mb-0">{course.title}</span>
                <span
                    className="fw-bold text-uppercase flex-shrink-0"
                    style={{ color: '#5E646980', cursor: 'pointer' }}
                    onClick={hideOverlay}
                >
              Cancel
            </span>
              </div>
              <div className="d-flex align-items-center mt-3">
            <span
                className="text-uppercase pe-1"
                style={{ color: '#5E6469BF' }}
            >
              Enroll Students:
            </span>
                <Badge bg={'light'}>{childrenSelectedCount} Selected</Badge>
              </div>
              <Stack gap={1}>
                <div className="children-select">
                  {userChildren?.length > 0 &&
                      userChildren.map((child, index) => renderChildRow(child, index))}
                </div>
                {isWaitlistsError && (
                  <div className="text-danger mt-2" role="alert">
                    Unable to load waitlist status. Please close and try again.
                  </div>
                )}
                {!isInviteFlow && (
                  <div className="children-select">
              <span
                  className="add-child"
                  onClick={addChild}
              >
                + Add another child to your account
              </span>
                  </div>
                )}
                <div className="course-information mt-4">
                  <div className="h3">
                <span className="text-info fw-bold">
                  Tuition: {formatToCurrency(courseValues.total)}
                </span>
                    {course.can_be_prorated && (
                        <>
                          -{' '}
                          <span className="prorated">
                      {formatToCurrency(courseValues.prorated)} Prorated
                    </span>
                        </>
                    )}
                  </div>
                  <div className="classes-remaining">
                    <LuChefHat className="icon" />
                    <span>
                  {classesRemaining.length} of {course.lessons.length} Classes remaining
                </span>
                    {course.can_be_prorated && (
                        <>
                          :{' '}
                          <span className="classes-total">
                      {formatToCurrency(course.price_per_class)} (x{childrenSelectedCount})
                    </span>
                        </>
                    )}
                  </div>
                  <div className="students-information">
                    <BsFillPersonFill className="icon" />
                    <span>
                  {childrenSelectedCount} student
                      {childrenSelectedCount > 1 ? 's' : ''}
                </span>
                  </div>
                </div>
              </Stack>
              <Stack
                  gap={2}
                  direction="horizontal"
                  className="mt-3 flex-column flex-sm-row"
              >
                <SimpleButton
                    type="button"
                    variant="outline-primary"
                    width={'100%'}
                    disabled={isSubmitting || !waitlistsReady}
                    onClick={async () => {
                      await handleSubmit(false);
                    }}
                >
                  Keep Shopping
                </SimpleButton>
                <SimpleButton
                    type="button"
                    variant="primary"
                    width={'100%'}
                    disabled={isSubmitting || !canSubmitOrReturn || !waitlistsReady}
                    onClick={async () => {
                      await handleSubmit(true);
                    }}
                >
                  {getSubmitLabel()}
                </SimpleButton>
              </Stack>
            </div>
        )}
      </>
  );
};

export default CourseRegister;